/**
 * CR229: T1 - Data Access Layer for Template Editor
 * CR405: Added safe string utilities to prevent toLowerCase/toUpperCase crashes
 * 
 * Single hook that provides all data needed by the Deal Cards template editor:
 * - Deal types from registry (sorted, enabled filter)
 * - Templates from deal_card_templates
 * - Template resolution with fallback
 * - Sample deal fetching for preview
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import type { DealTypeRegistryEntry, DealCardTemplate, CardTemplateJson } from "@/providers/DealCardConfigProvider";
import { getTemplateForDealType, hasExactTemplate, getDefaultTemplateForType, type TemplateResolutionResult } from "@/lib/deals/templateResolver";
import { sanitizeDealCardJsonForV2 } from "@/lib/deal-cards/specV2/themeOwnedGuard";
import { safeStr, safeUpper } from "@/lib/safeString";

// ================================================================
// TYPES
// ================================================================

export interface DealCardTemplateEditorData {
  // Registry
  dealTypes: DealTypeRegistryEntry[];
  enabledDealTypes: DealTypeRegistryEntry[];
  
  // Templates
  templates: DealCardTemplate[];
  templatesByDealTypeKey: Map<string, CardTemplateJson>;
  
  // Active selection
  activeDealTypeKey: string;
  setActiveDealTypeKey: (key: string) => void;
  
  // Template resolution for active type
  activeTemplateResolution: TemplateResolutionResult;
  hasExactTemplateForActive: boolean;
  
  // Loading state
  loading: boolean;
  error: string | null;
  
  // Actions
  refetch: () => Promise<void>;
  createTemplateFromDefault: (dealTypeKey: string) => Promise<boolean>;
  duplicateTemplate: (sourceDealTypeKey: string, targetDealTypeKey: string) => Promise<boolean>;
}

// ================================================================
// HOOK
// ================================================================

export function useDealCardTemplateEditor(
  initialDealType: string = "BONUS"
): DealCardTemplateEditorData {
  // State
  const [dealTypes, setDealTypes] = useState<DealTypeRegistryEntry[]>([]);
  const [templates, setTemplates] = useState<DealCardTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeDealTypeKey, setActiveDealTypeKey] = useState(initialDealType);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [registryResult, templatesResult] = await Promise.all([
        supabase
          .from("deal_type_registry")
          .select("*")
          .order("sort_order", { ascending: true }),
        supabase
          .from("deal_card_templates")
          .select("*"),
      ]);
      
      if (registryResult.error) {
        throw new Error(`Registry fetch error: ${registryResult.error.message}`);
      }
      if (templatesResult.error) {
        throw new Error(`Templates fetch error: ${templatesResult.error.message}`);
      }
      
      setDealTypes(registryResult.data || []);
      setTemplates((templatesResult.data || []).map(t => ({
        ...t,
        template_json: t.template_json as unknown as CardTemplateJson,
      })));
      
    } catch (err) {
      console.error("[useDealCardTemplateEditor] Fetch error:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Derived: Enabled deal types only
  const enabledDealTypes = useMemo(() => {
    return dealTypes.filter(dt => dt.is_enabled);
  }, [dealTypes]);

  // Derived: Templates map
  const templatesByDealTypeKey = useMemo(() => {
    const map = new Map<string, CardTemplateJson>();
    for (const t of templates) {
      map.set(t.deal_type_key, t.template_json);
    }
    return map;
  }, [templates]);

  // Derived: Active template resolution
  const activeTemplateResolution = useMemo(() => {
    return getTemplateForDealType(activeDealTypeKey, templatesByDealTypeKey);
  }, [activeDealTypeKey, templatesByDealTypeKey]);

  // Derived: Has exact template
  const hasExactTemplateForActive = useMemo(() => {
    return hasExactTemplate(activeDealTypeKey, templatesByDealTypeKey);
  }, [activeDealTypeKey, templatesByDealTypeKey]);

  // Action: Create template from default
  const createTemplateFromDefault = useCallback(async (dealTypeKey: string): Promise<boolean> => {
    try {
      const defaultTemplate = getDefaultTemplateForType(dealTypeKey);
      // CR315: Apply write-guard sanitizer
      const sanitizedTemplate = sanitizeDealCardJsonForV2(defaultTemplate);
      // CR407: Use safeUpper to prevent crash on undefined
      const safeDealTypeKey = safeUpper(dealTypeKey) || "BONUS";
      
      const { error: insertError } = await supabase
        .from("deal_card_templates")
        .upsert([{
          deal_type_key: safeDealTypeKey,
          template_json: JSON.parse(JSON.stringify(sanitizedTemplate)) as Json,
          updated_at: new Date().toISOString(),
        }], {
          onConflict: "deal_type_key",
        });
      
      if (insertError) {
        console.error("[createTemplateFromDefault] Insert error:", insertError);
        return false;
      }
      
      await fetchData();
      return true;
    } catch (err) {
      console.error("[createTemplateFromDefault] Error:", err);
      return false;
    }
  }, [fetchData]);

  // Action: Duplicate template
  const duplicateTemplate = useCallback(async (
    sourceDealTypeKey: string, 
    targetDealTypeKey: string
  ): Promise<boolean> => {
    try {
      // CR407: Use safeUpper to prevent crash on undefined
      const safeSourceKey = safeUpper(sourceDealTypeKey) || "BONUS";
      const safeTargetKey = safeUpper(targetDealTypeKey) || "BONUS";
      
      const sourceTemplate = templatesByDealTypeKey.get(safeSourceKey);
      if (!sourceTemplate) {
        console.error("[duplicateTemplate] Source template not found");
        return false;
      }
      
      // CR315: Apply write-guard sanitizer
      const sanitizedTemplate = sanitizeDealCardJsonForV2(sourceTemplate);
      
      const { error: insertError } = await supabase
        .from("deal_card_templates")
        .upsert([{
          deal_type_key: safeTargetKey,
          template_json: JSON.parse(JSON.stringify(sanitizedTemplate)) as Json,
          updated_at: new Date().toISOString(),
        }], {
          onConflict: "deal_type_key",
        });
      
      if (insertError) {
        console.error("[duplicateTemplate] Insert error:", insertError);
        return false;
      }
      
      await fetchData();
      return true;
    } catch (err) {
      console.error("[duplicateTemplate] Error:", err);
      return false;
    }
  }, [templatesByDealTypeKey, fetchData]);

  return {
    dealTypes,
    enabledDealTypes,
    templates,
    templatesByDealTypeKey,
    activeDealTypeKey,
    setActiveDealTypeKey,
    activeTemplateResolution,
    hasExactTemplateForActive,
    loading,
    error,
    refetch: fetchData,
    createTemplateFromDefault,
    duplicateTemplate,
  };
}

// ================================================================
// SAMPLE DEALS HOOK (CR311: production-safe view)
// ================================================================

export interface SampleDeal {
  id: string;
  merchant: string;
  headlineSnippet: string;
  dealType: string;
  sourceType: string;
  /** Full canonical deal JSON for rendering */
  rawData: Record<string, unknown>;
  /** CR311: Precomputed value fields for validation */
  computedValueCad: number | null;
  effectivePercentBack: number | null;
  validFrom: string | null;
  validUntil: string | null;
}

/**
 * CR308: Data source mode for deal previews.
 */
export type DealDataSourceMode = "sample" | "live";

/**
 * CR308/CR311: Fetch deals from either Sample or Live view.
 * - Sample: v_layout_studio_sample_deals
 * - Live: v_deals_live
 */
export function useSampleDeals(
  dealTypeKey: string | null,
  limit: number = 25,
  mode: DealDataSourceMode = "sample"
): {
  samples: SampleDeal[];
  loading: boolean;
  error: string | null;
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  selectedDeal: Record<string, unknown> | null;
  mode: DealDataSourceMode;
  viewName: string;
} {
  const [samples, setSamples] = useState<SampleDeal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // CR308: Determine view name based on mode
  const viewName = mode === "live" ? "v_deals_live" : "v_layout_studio_sample_deals";

  // CR327: Separate fetch function without selectedId dependency
  // to prevent refetches when selection changes
  const fetchSamples = useCallback(async () => {
    if (!dealTypeKey) {
      setSamples([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    // CR327: Increment debug counter
    try {
      const { incrementPreviewCounter } = await import("@/components/admin/deal-cards/PreviewStabilityCounters");
      incrementPreviewCounter("sampleFetches");
    } catch {
      // Counter module may not be loaded in production
    }
    
    try {
      // CR308: Type for unified deal row shape (both views have same columns)
      type DealRow = {
        id: string;
        canonical_deal_json: Record<string, unknown> | null;
        deal_type: string | null;
        source_type: string | null;
        computed_value_cad: number | null;
        effective_percent_back: number | null;
        valid_from: string | null;
        valid_until: string | null;
      };
      
      // CR308: Query using raw SQL to avoid type issues with dynamic view selection
      // Both v_deals_live and v_layout_studio_sample_deals have the same column shape
      const selectCols = "id, canonical_deal_json, deal_type, source_type, computed_value_cad, effective_percent_back, valid_from, valid_until";
      
      // Use the sample view by default, or live view
      // Since TypeScript types may be stale, use explicit table name
      const query = mode === "live"
        ? supabase
            .from("v_deals_live" as "v_layout_studio_sample_deals") // Type cast for TS
            .select(selectCols)
            .order("id", { ascending: false })
            .limit(limit)
        : supabase
            .from("v_layout_studio_sample_deals")
            .select(selectCols)
            .order("id", { ascending: false })
            .limit(limit);
      
      // Filter by deal_type - CR405: Use shared safeUpper utility
      const safeTypeKey = safeStr(dealTypeKey, "");
      const filteredQuery = (safeTypeKey && safeTypeKey !== "ALL")
        ? query.eq("deal_type", safeUpper(safeTypeKey))
        : query;
      
      const { data, error: fetchError } = await filteredQuery;
      
      if (fetchError) {
        throw new Error(fetchError.message);
      }
      
      const mapped: SampleDeal[] = ((data || []) as DealRow[]).map(row => {
        const json = (row.canonical_deal_json || {}) as Record<string, unknown>;
        // CR410: Use safeStr to prevent crashes on non-string values
        const merchantKey = safeStr(json.merchant_key) || safeStr(json.merchant_name) || "Unknown";
        const headline = safeStr(json.headline_mechanic) || safeStr(json.offer_text) || "No headline";
        
        return {
          id: row.id,
          merchant: merchantKey,
          headlineSnippet: truncate(headline, 40),
          dealType: row.deal_type || dealTypeKey || "UNKNOWN",
          sourceType: row.source_type || "unknown",
          // CR434: Use mergeCanonicalDeal pattern — json spread first, then canonical_deal_json ref, id forced last
          rawData: { ...json, canonical_deal_json: json, id: row.id },
          // CR311: Include validation fields from view columns
          computedValueCad: row.computed_value_cad,
          effectivePercentBack: row.effective_percent_back,
          validFrom: row.valid_from,
          validUntil: row.valid_until,
        };
      });
      
      setSamples(mapped);
      
      // CR327: Auto-select first only on initial load (not on refetch)
      // Use a ref to track if we've done initial selection
      if (mapped.length > 0 && !selectedId) {
        setSelectedId(mapped[0].id);
      }
    } catch (err) {
      console.error("[useSampleDeals] Error:", err);
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  // CR327: Remove selectedId from deps - it should NOT trigger refetch
  }, [dealTypeKey, limit, mode]);

  useEffect(() => {
    fetchSamples();
  }, [fetchSamples]);

  // Find selected deal
  const selectedDeal = useMemo(() => {
    if (!selectedId) return null;
    const sample = samples.find(s => s.id === selectedId);
    return sample?.rawData || null;
  }, [selectedId, samples]);

  return {
    samples,
    loading,
    error,
    selectedId,
    setSelectedId,
    selectedDeal,
    mode,
    viewName,
  };
}

/**
 * CR311: Get full sample with validation fields (not just rawData).
 */
export function useSelectedSampleFull(
  samples: SampleDeal[],
  selectedId: string | null
): SampleDeal | null {
  return useMemo(() => {
    if (!selectedId) return null;
    return samples.find(s => s.id === selectedId) || null;
  }, [samples, selectedId]);
}

function truncate(str: string, maxLen: number): string {
  if (str.length <= maxLen) return str;
  return str.slice(0, maxLen - 3) + "...";
}
